"use client";

import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Volume2, VolumeX } from "lucide-react";
import BOOKS from "@/app/books/data";
import TextGenerateEffect from "@/components/TextGenerateEffect";

const INTRO = {
  type: "intro",
  kicker: "Published Works",
  title: "Books written by Burak Ozgur",
  summary:
    "A career marked by 14 years as an LA Super Doctor, 7 years as an OC Physician of Excellence, 151 global presentations, 34 peer‑reviewed papers, 10 book chapters, and three published books.",
  videoId: "lPpKMGMyBIY",
};

// Short card-face blurb per book — hand-curated, not the full
// description from books/data.js. Desktop-only (hidden on mobile to
// keep the card heights manageable).
const SUMMARY_BY_SLUG = {
  "grabbing-life-by-the-horns":
    "Prepare to be captivated as Dr. Ozgur shares his understanding and insights about the fascinating complexity of the human brain, and the resilience of the human spirit.",
  "pugslys-idea-goes-viral":
    "Life couldn't be better for our charming pug-tagonist and his quirky petmates until a group of raccoons shares a startling revelation about humans.",
  "minimally-invasive-spine-surgery":
    "Written by leaders in both neurologic and orthopedic spine surgery, this book offers the most up-to-date material and the broadest perspective on the subject.",
};

const CARDS = [
  INTRO,
  ...BOOKS.map((b) => ({ type: "book", summary: SUMMARY_BY_SLUG[b.slug], ...b })),
];

export default function StickyCardsSection() {
  const sectionRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [titleTriggerIndex, setTitleTriggerIndex] = useState(0);
  const [origin, setOrigin] = useState("");
  const shortIframeRef = useRef(null);

  useEffect(() => {
    if (typeof window !== "undefined") setOrigin(window.location.origin);
  }, []);

  // Mute/unmute + force-play. The on-load sync is deferred 800ms so
  // YouTube's internal message handler has time to initialize
  // (sending too early throws isExternalMethodAvailable inside
  // YouTube). User-click sync fires immediately.
  useEffect(() => {
    const iframe = shortIframeRef.current;
    if (!iframe) return;

    const sendCommand = (func) => {
      if (!iframe.contentWindow) return;
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func, args: [] }),
        "*"
      );
    };

    const syncState = () => {
      sendCommand(muted ? "mute" : "unMute");
      sendCommand("playVideo");
    };

    syncState();
    const onLoad = () => setTimeout(syncState, 800);
    iframe.addEventListener("load", onLoad);
    return () => iframe.removeEventListener("load", onLoad);
  }, [muted]);

  // First user gesture unlocks playback on iOS — kick the iframe then.
  useEffect(() => {
    let fired = false;
    const kick = () => {
      if (fired) return;
      fired = true;
      const iframe = shortIframeRef.current;
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: "command", func: "playVideo", args: [] }),
          "*"
        );
      }
    };
    const opts = { once: true, passive: true };
    window.addEventListener("touchstart", kick, opts);
    window.addEventListener("click", kick, opts);
    window.addEventListener("scroll", kick, opts);
    return () => {
      window.removeEventListener("touchstart", kick);
      window.removeEventListener("click", kick);
      window.removeEventListener("scroll", kick);
    };
  }, []);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray(".ba-card");
      const totalCards = cards.length;
      const segmentSize = 1 / totalCards;
      const cardYOffset = 5;
      const cardScaleStep = 0.075;

      cards.forEach((card, i) => {
        gsap.set(card, {
          xPercent: -50,
          yPercent: -50 + i * cardYOffset,
          scale: 1 - i * cardScaleStep,
        });
      });

      ScrollTrigger.create({
        trigger: section,
        start: "top top",
        end: () => {
          // Mobile gets slightly less scroll per card (0.9) but not
          // enough to feel hurried. Desktop uses the full 1.0.
          const scale = window.innerWidth < 768 ? 0.9 : 1;
          return `+=${window.innerHeight * totalCards * scale}px`;
        },
        pin: true,
        pinSpacing: true,
        // Soft scrub (not rigid 1:1) — smooths out momentum-scroll
        // jitter on mobile Safari so cards don't snap when the user
        // flicks past. `anticipatePin` is dropped because it visibly
        // pre-applies the pin one frame early on touch devices.
        scrub: 0.4,
        invalidateOnRefresh: true,
        fastScrollEnd: true,
        onUpdate: (self) => {
          const progress = self.progress;
          // Dismiss the #library CTA before the pin fully releases so the
          // dock doesn't linger into the Highlights section.
          section.dataset.ctxActive = progress < 0.8 ? "true" : "false";
          const currentIndex = Math.min(
            Math.floor(progress / segmentSize),
            totalCards - 1
          );
          const segProgress = Math.min(
            1,
            (progress - currentIndex * segmentSize) / segmentSize
          );

          // Publish the current card index to React so child
          // TextGenerateEffects can fire per-card instead of all at once.
          setActiveIndex(currentIndex);

          // Title trigger shifts to the next card earlier than the card
          // physically takes the front — once the current card is ~40%
          // dismissed, the next title already starts animating in.
          const nextTitleIndex = Math.min(
            Math.floor(progress / segmentSize + 0.6),
            totalCards - 1
          );
          setTitleTriggerIndex(nextTitleIndex);

          cards.forEach((card, i) => {
            if (i < currentIndex) {
              gsap.set(card, { yPercent: -250, rotationX: 35 });
            } else if (i === currentIndex) {
              gsap.set(card, {
                yPercent: gsap.utils.interpolate(-50, -250, segProgress),
                rotationX: gsap.utils.interpolate(0, 35, segProgress),
                scale: 1,
              });
            } else {
              const behindIndex = i - currentIndex;
              const currentYOffset = (behindIndex - segProgress) * cardYOffset;
              const currentScale = 1 - (behindIndex - segProgress) * cardScaleStep;
              gsap.set(card, {
                yPercent: -50 + currentYOffset,
                rotationX: 0,
                scale: currentScale,
              });
            }
          });
        },
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="library" className="ba-sticky-cards">
      {CARDS.map((card, index) => {
        const num = String(index + 1).padStart(2, "0");
        return (
          <article
            key={card.id || `card-${index}`}
            className="ba-card"
            style={{ zIndex: CARDS.length + 1 - index }}
          >
            <span className="ba-num">({num})</span>

            {card.type === "intro" ? (
              <>
                <div className="ba-col">
                  <p>{card.kicker}</p>
                  <div className="ba-book-foot">
                    {card.summary && (
                      <TextGenerateEffect
                        as="p"
                        className="ba-summary"
                        words={card.summary}
                        duration={0.7}
                        staggerDelay={0.025}
                        filter={false}
                        highlightColor="#fff"
                        trigger={index === titleTriggerIndex}
                      />
                    )}
                    <TextGenerateEffect
                      as="h3"
                      words={card.title}
                      duration={0.9}
                      staggerDelay={0.08}
                      filter={false}
                      highlightColor="#fff"
                      trigger={index === titleTriggerIndex}
                    />
                  </div>
                </div>
                <div className="ba-col ba-col-video">
                  <iframe
                    ref={shortIframeRef}
                    src={`https://www.youtube.com/embed/${card.videoId}?autoplay=1&mute=1&controls=0&modestbranding=1&loop=1&playlist=${card.videoId}&playsinline=1&enablejsapi=1&rel=0${origin ? `&origin=${encodeURIComponent(origin)}` : ""}`}
                    title={card.title}
                    className="ba-short-iframe"
                    allow="autoplay; encrypted-media; picture-in-picture"
                  />
                  <button
                    type="button"
                    onClick={() => setMuted((m) => !m)}
                    aria-label={muted ? "Unmute video" : "Mute video"}
                    className="ba-mute-btn"
                  >
                    {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="ba-col">
                  <p>{card.genre}</p>
                  <div className="ba-book-foot">
                    {card.summary && (
                      <TextGenerateEffect
                        as="p"
                        className="ba-summary"
                        words={card.summary}
                        duration={0.7}
                        staggerDelay={0.025}
                        filter={false}
                        highlightColor="#fff"
                        trigger={index === titleTriggerIndex}
                      />
                    )}
                    <TextGenerateEffect
                      as="h3"
                      words={card.title}
                      duration={0.9}
                      staggerDelay={0.08}
                      filter={false}
                      highlightColor="#fff"
                      trigger={index === titleTriggerIndex}
                    />
                    <p className="ba-author">by {card.author}</p>
                    <div className="ba-actions">
                      <a
                        href={card.paperback || card.kindle}
                        target="_blank"
                        rel="noreferrer"
                        className="ba-btn ba-btn-amazon"
                      >
                        <span>Buy on Amazon</span>
                      </a>
                    </div>
                  </div>
                </div>
                <div className="ba-col ba-col-cover">
                  <img src={card.image} alt={card.title} />
                </div>
              </>
            )}
          </article>
        );
      })}
    </section>
  );
}
